import { useMemo } from "react";
import { UpdateCardData } from "entities/AirSavedCard/api/query.types";
import { AirSavedCardModel, airSavedCardStore } from "entities/AirSavedCard";
import { useUpdateSingleCardMutation } from "entities/AirSavedCard";
import { formatAirCargo } from "entities/AirSavedCard/api/cargo";
import { AirSearchCardModel, createAirSearchQueryParams, useSearchQuery } from "entities/AirSearchCard";
import { useBulkUpdateSearchQuery } from "entities/AirSearchCard";
import { appStore } from "entities/App";
import { authStore } from "entities/Auth";
import { resolveDateValues } from "entities/shared/utils";
import { subscriptionStore } from "entities/Subscription";
import { useAppDispatch, useAppSelector } from "shared/lib/redux";
import { Nullable } from "shared/lib/typeCheck.lib";
import { getUtcTimestamp } from "shared/utils/dateTime.utils";

import { getFollowCardPin, mapAirStatusForSaved } from "../utils";

type ShouldUpdateCardFunc = () => boolean;
type UpdateCardFunc = (model: AirSavedCardModel) => void;
type UpdateAllCardsFunc = (cards: AirSavedCardModel[]) => Promise<{
  results: { reference: string | null; isSuccess: boolean }[];
  canUpdateMore: boolean;
}>;
type UsePrepareCardUpdateActionsReturnType = {
  updateCard: UpdateCardFunc;
  updateAllCards: UpdateAllCardsFunc;
  shouldUpdateCard: ShouldUpdateCardFunc;
  isResolvingUpdatePossibility: boolean;
  isCardUpdateSuccess: boolean;
  isCardUpdating: boolean;
};

type UseAirCardUpdateActionsParams = {
  model?: Nullable<AirSavedCardModel>;
  isOpenedCardExist?: boolean;
  openedCardUpdateAction?: (response: AirSavedCardModel) => {
    type: string;
    payload: AirSavedCardModel | AirSearchCardModel | null;
  };
  updateFilteredCards?: (response: AirSavedCardModel) => {
    type: string;
    payload: AirSavedCardModel;
  };
  updateCardByTagAction?: (response: AirSavedCardModel) => {
    type: string;
    payload: AirSavedCardModel;
  };
};

export const createAirUpdateCardHook = ({
  model,
  isOpenedCardExist,
  openedCardUpdateAction,
  updateFilteredCards,
  updateCardByTagAction,
}: UseAirCardUpdateActionsParams): (() => UsePrepareCardUpdateActionsReturnType) => {
  return function useSavedCardUpdateActions() {
    const dispatch = useAppDispatch();
    const isAuthenticated = useAppSelector(authStore.selectors.isAuthenticated);

    const { searchRequest: singleSearchRequest, isSearchLoading: isSingleSearchLoading } = useSearchQuery({
      queryKey: [`card-update-request-${model?.reference}`],
      isAuthenticated,
      setSubscription: (subscription) => ({
        type: subscriptionStore.actions.setSubscription.type,
        payload: subscription,
      }),
      setActivePaymentModal: (activePaymentModal) => ({
        type: subscriptionStore.actions.setActivePaymentModal.type,
        payload: activePaymentModal,
      }),
      setAppIntegrationRequestLimitReached: (appIntegrationRequestLimitReached) => ({
        type: appStore.actions.setAppIntegrationRequestLimitReached.type,
        payload: appIntegrationRequestLimitReached,
      }),
      setWebAppRequestLimitReached: (webAppRequestLimitReached) => ({
        type: appStore.actions.setWebAppRequestLimitReached.type,
        payload: webAppRequestLimitReached,
      }),
    });

    // hook for bulk update
    const { searchRequest: bulkSearchRequest, isSearchLoading: isBulkSearchLoading } = useBulkUpdateSearchQuery({
      isAuthenticated,
      setSubscription: (subscription) => ({
        type: subscriptionStore.actions.setSubscription.type,
        payload: subscription,
      }),
      setActivePaymentModal: (activePaymentModal) => ({
        type: subscriptionStore.actions.setActivePaymentModal.type,
        payload: activePaymentModal,
      }),
      setAppIntegrationRequestLimitReached: (appIntegrationRequestLimitReached) => ({
        type: appStore.actions.setAppIntegrationRequestLimitReached.type,
        payload: appIntegrationRequestLimitReached,
      }),
      setWebAppRequestLimitReached: (webAppRequestLimitReached) => ({
        type: appStore.actions.setWebAppRequestLimitReached.type,
        payload: webAppRequestLimitReached,
      }),
    });

    const { updateCard: updateCardRequest, isCardUpdateSuccess, isCardUpdateLoading } = useUpdateSingleCardMutation();

    const isResolvingUpdatePossibility = useMemo(
      () => isSingleSearchLoading || isBulkSearchLoading || isCardUpdateLoading,
      [isSingleSearchLoading, isBulkSearchLoading, isCardUpdateLoading]
    );

    const prepareUpdateData = (data: AirSearchCardModel): UpdateCardData => {
      if (!model || !data) return {};
      const { atd, etd, ata, eta } = data;
      const { departureDate, arrivalDate } = resolveDateValues({ atd, etd, ata, eta });

      const preparedData: UpdateCardData = {
        location: {
          pin: getFollowCardPin(data),
          from: data.directionFrom ?? null,
          to: data.directionTo ?? null,
          transport: "AIR",
        },
        carrier_scac: data.iataCode || null,
        carrier_name: data.airlineName || null,
        status: mapAirStatusForSaved(data.status),
        equipments_count: data.piece,
        size_type: formatAirCargo({ pieces: data.piece, weight: data.weight }),
        tags: model.tags,
        date: {
          from_actual: departureDate.isActual,
          from: departureDate.value,
          to_actual: arrivalDate.isActual,
          to: arrivalDate.value,
          to_predictive: null,
        },
      };

      return preparedData;
    };

    const shouldUpdateCard: ShouldUpdateCardFunc = () => {
      // if (!isAuthenticated || !model || !airlines || !airlines?.length) return false;
      if (!isAuthenticated || !model) return false;

      const dateValueToCompare = model.updatedAt;
      if (!dateValueToCompare) return false;

      const dateToCompare = getUtcTimestamp(dateValueToCompare);

      // const airline = model.airlineName;
      // if (!airline) return false;

      // const foundCarrierItem = airlines.find((d) => d.carrier_iata.includes(airline));
      // if (!foundCarrierItem) return false;

      const cacheInterval = 3600 * 4; // 4 hours
      const cacheIntervalMilliseconds = cacheInterval * 1000;
      const currentDate = getUtcTimestamp(new Date());
      return dateToCompare + cacheIntervalMilliseconds < currentDate;
    };

    const updateCard: UpdateCardFunc = (model) => {
      const reqParams = createAirSearchQueryParams({
        model: {
          reference: model.reference,
        },
        requestType: "updateCard",
      });

      if (!model || !reqParams || isResolvingUpdatePossibility) return;

      singleSearchRequest(reqParams, {
        onSuccess: (searchResponse) => {
          updateCardRequest(
            { id: model.id, data: prepareUpdateData(searchResponse) },
            {
              onSuccess: (response) => {
                dispatch(airSavedCardStore.actions.updateSavedCardAction(response));
                updateFilteredCards && dispatch(updateFilteredCards(response));

                if (isOpenedCardExist && openedCardUpdateAction) {
                  dispatch(openedCardUpdateAction(response));
                }
                updateCardByTagAction && dispatch(updateCardByTagAction(response));
              },
            }
          );
        },
      });
    };

    const updateAllCards: UpdateAllCardsFunc = async (cards) => {
      const results: { reference: string | null; isSuccess: boolean }[] = [];

      for (const card of cards) {
        const reqParams = createAirSearchQueryParams({
          model: {
            reference: card.reference,
          },
          requestType: "updateCard",
        });

        if (!reqParams || isResolvingUpdatePossibility) {
          results.push({ reference: card.reference, isSuccess: false });
          continue;
        }

        try {
          const searchResponse = await new Promise<AirSearchCardModel | null>((resolve, reject) => {
            bulkSearchRequest(reqParams, {
              onSuccess: (response) => resolve(response),
              onError: () => reject(null),
            });
          });

          if (!searchResponse) {
            results.push({ reference: card.reference, isSuccess: false });
            continue;
          }

          if (searchResponse?.requestLimitReachedWebApp) {
            results.push({ reference: card.reference, isSuccess: false });
            return { results, canUpdateMore: false };
          }

          const response = await new Promise<AirSavedCardModel>((resolve) => {
            updateCardRequest(
              { id: card.id, data: prepareUpdateData(searchResponse) },
              {
                onSuccess: (result) => resolve(result),
              }
            );
          });
          results.push({ reference: card.reference, isSuccess: true });
          dispatch(airSavedCardStore.actions.updateSavedCardAction(response));
          updateFilteredCards && dispatch(updateFilteredCards(response));
          updateCardByTagAction && dispatch(updateCardByTagAction(response));
        } catch (_) {
          results.push({ reference: card.reference, isSuccess: false });
          continue;
        }
      }
      return { results, canUpdateMore: true };
    };

    return {
      updateCard,
      updateAllCards,
      shouldUpdateCard,
      isResolvingUpdatePossibility,
      isCardUpdateSuccess,
      isCardUpdating: isCardUpdateLoading,
    };
  };
};
